import React, { MouseEvent, useEffect, useRef, useState } from 'react';
import { StoryblokComponent } from './types';
import { TeaserService } from '../../services/teaser';
import { PageItem } from '../../services/teaser/types';
import { StringService } from '../../services/string';
import { StoryblokService } from '../../services/storyblok';
import { blokToComponent } from '../../helpers/blok-to-component';
import { ConditionalWrapper } from '../../helpers/conditional-wrapper';
import { filterProps } from '../../helpers/filter-props';
import { parseAssetSource } from '../../utils/asset';
import { BaseBlok, BlokToComponentProps } from '../../helpers/blok-to-component/types';
import { RocheTeaser as RocheTeaserType } from '../../types/global';
import { RocheUITeaser, RocheUITeaserLayout } from '@repo/roche-ui';
import { useTranslation } from '../../hooks/use-translation';
import { handleRocheTeaserClick } from '../../helpers/roche-teaser-tracking';

type RocheTeaserManualProps = BlokToComponentProps<
  RocheTeaserType &
    BaseBlok & {
      page: { story: { id: number; preRenderContent?: any } };
    }
>;

const warningDescription = `
  If you wish to proceed with this action, please publish the linked page first.
  Note that if the linked page on the teaser remains unpublished,
  this teaser will not show up on the live version of the page.
`;

const renderWarning = (
  keyForChildren: string | number,
  slot: string,
  teaserRef: React.MutableRefObject<HTMLDivElement>,
) => (
  <RocheUITeaser
    ref={teaserRef}
    key={keyForChildren}
    layout={RocheUITeaserLayout.VARIANT_B_WITHOUT_IMAGE}
    teaserTitle='WARNING: This teaser is not published!'
    teaserDescription={warningDescription}
    slot={slot}
    unpublished={true}
    loaded={true}
  />
);

export const RocheTeaser = ({
  blok,
  updateHoistedState,
  wrapperTag,
  keyForChildren,
  slot,
  getComponent,
}: RocheTeaserManualProps): JSX.Element => {
  const [teaser, setTeaser] = useState<PageItem>(blok?.page?.story?.preRenderContent || ({} as PageItem));
  const Wrapper = wrapperTag as React.ElementType;
  const teaserRef = useRef<HTMLDivElement>(null);
  const readMoreTranslation = useTranslation('read more');
  const [hasUpdatedHoistedState, setHasUpdatedHoistedState] = useState(false);

  useEffect(() => {
    const storyId = blok.page?.story?.id;
    const hasTeaser = teaser && Object.keys(teaser).length > 0;
    const { _uid: blokUid } = blok;
    if (hasUpdatedHoistedState) {
      // if we have already informed the parent component we return
      return;
    }

    if (storyId && hasTeaser) {
      // if we have the teaser already, we inform the parent component and return
      updateHoistedState?.(1, 1, blokUid, teaser);
      setHasUpdatedHoistedState(true);
      return;
    }

    if (storyId && !hasTeaser) {
      // if we do not have the teaser we fetch it and inform the parent component
      TeaserService.getTeaser(storyId).then((result) => {
        if (!teaserRef.current) {
          return;
        }
        if (result) {
          setTeaser(result);
          updateHoistedState?.(1, 1, blokUid, result);
          setHasUpdatedHoistedState(true);
          return;
        }
        // if we do not get a result we inform the parent component
        // (e.g. teaser-slider) so that it can initialize with a warning element
        updateHoistedState?.(1, 1, '', {});
        setHasUpdatedHoistedState(true);
      });
    }

    // even in live-mode we get the unpublished teaser, we need to inform the parent component
    // so that the parent (e.g. teaser-slider) has the correct number of teasers that are ready
    // A better solution would be if we only got the teasers that are published in live-mode.
    if (!storyId && !StoryblokService.isInEditor()) {
      updateHoistedState?.(1, 1, '', {});
      setHasUpdatedHoistedState(true);
    }
  }, [teaser, blok, hasUpdatedHoistedState]);

  const { slotted } = filterProps(blok);
  const slottedComponents = Object.keys(slotted).flatMap((key) =>
    (slotted[key] as StoryblokComponent[]).map((component) =>
      component.component === 'roche-button'
        ? {
            ...component,
            slot: key,
            'no-margin': blok.layout.indexOf('teaser--card') === 0,
            appearance: 'tertiary',
          }
        : {
            ...component,
            slot: key,
          },
    ),
  );
  const teaserImage = teaser.content?.teaser_image?.[0];
  const hasTeaser = teaser && Object.keys(teaser).length > 0;
  const isTeaserInDraft = !hasTeaser;

  const secondaryButton = slottedComponents.map((component, index) =>
    blokToComponent({ blok: { ...component }, getComponent }, `${index}`),
  )[0];

  return (
    <ConditionalWrapper
      key={keyForChildren}
      condition={wrapperTag !== undefined && wrapperTag !== ''}
      wrapper={(children: JSX.Element): JSX.Element => <Wrapper slot={slot}>{children}</Wrapper>}
    >
      {isTeaserInDraft && StoryblokService.isInEditor() ? (
        renderWarning(keyForChildren, slot, teaserRef)
      ) : (
        <RocheUITeaser
          ref={teaserRef}
          key={keyForChildren}
          layout={blok.layout}
          teaserImage={teaserImage?.brightcove_id || parseAssetSource(teaserImage?.src.filename)}
          teaserTitle={teaser.content?.teaser_title}
          teaserOverline={
            RocheUITeaserLayout.VARIANT_C_WITHOUT_IMAGE_WITH_DATE === blok.layout
              ? teaser?.first_published_at
              : teaser.content?.teaser_overline
          }
          loaded={teaser.content !== undefined}
          teaserDescription={teaser.content?.teaser_description}
          teaserUrl={StringService.sanitizeUrl(`/${teaser?.full_slug}`)}
          slot={slot}
          isSearchResult={slot === 'quick-links'}
          isTeaserInDraft={isTeaserInDraft}
          primaryButtonText={StringService.capitalize(readMoreTranslation)}
          secondaryButton={secondaryButton}
          onTeaserClick={(e: MouseEvent) =>
            handleRocheTeaserClick(
              e,
              teaser.content?.teaser_title,
              StringService.sanitizeUrl(`/${teaser?.full_slug}`),
              teaserRef,
            )
          }
        />
      )}
    </ConditionalWrapper>
  );
};
